// [PURPOSE]: Compute the maximum number of magic items a character can attune
//            to, honoring Artificer's Magic Item Adept / Savant / Master class
//            features.
//
// [WHY]: Q26 audit (2026-05-16) flagged that `EquipmentPanel.js:918` and
//        `MagicItemsTab.js:112` both hardcoded `maxAttunement = 3`. RAW DMG
//        p.138 gives the default of 3 magic items. RAW TCoE p.11-12 raises
//        that cap for Artificers: L10 → 4 (Magic Item Adept), L14 → 5 (Magic
//        Item Savant), L18 → 6 (Magic Item Master). A L20 Artificer could
//        not attune a 4th item in the UI; a live UI bug.
//
// [INTEGRATION]: Imported by both `src/components/EquipmentPanel.js`
//                (Attunement Slots tracker) and
//                `src/components/tabs/MagicItemsTab.js` (Attunement Slots
//                header). Rule 11 — data-driven, no hardcoded class string
//                outside the canonical case-insensitive match.

/**
 * The default RAW attunement cap for any character (DMG p.138).
 */
export const DEFAULT_MAX_ATTUNEMENT = 3;

/**
 * Returns the maximum number of items the character can attune to.
 *
 * Honors the Artificer attunement-cap scaling from TCoE p.11-12:
 *   - L1-L9: 3 (default)
 *   - L10-L13: 4 (Magic Item Adept)
 *   - L14-L17: 5 (Magic Item Savant)
 *   - L18+: 6 (Magic Item Master)
 *
 * Multiclass: looks for the Artificer entry by case-insensitive className
 * match. A Fighter 5 / Artificer 10 has Artificer level 10 → returns 4.
 *
 * Defensive: missing `playerStats` or `classes` array returns the default 3.
 *
 * @param {object|null} playerStats - Character data with `.classes` array.
 * @returns {number} Max attunement (3, 4, 5, or 6).
 */
export function getMaxAttunement(playerStats) {
  if (!playerStats || !Array.isArray(playerStats.classes)) {
    return DEFAULT_MAX_ATTUNEMENT;
  }
  const artificerEntry = playerStats.classes.find(
    c => String(c?.className || '').toLowerCase() === 'artificer'
  );
  if (!artificerEntry) return DEFAULT_MAX_ATTUNEMENT;
  const level = artificerEntry.level || 0;
  if (level >= 18) return 6;
  if (level >= 14) return 5;
  if (level >= 10) return 4;
  return DEFAULT_MAX_ATTUNEMENT;
}
